import { Typography } from '@/components/atoms/Typography';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useIsAdhaJourney, useTranslation, useUIColors } from '@/core/hooks';
import { useAppStore } from '@/core/stores/appStore';
import { IdProofCard } from '@/modules/profile/components/IdProofCard';
import { router } from 'expo-router';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export default function IDProofScreen() {
    const { t } = useTranslation();
    const isAdhaJourney = useIsAdhaJourney();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const user = useAppStore((state) => state.auth.user);

    useCatalogDynatraceView(DT_SCREEN.ID_PROOF, 'id_proof_viewed', true);
    useCatalogDynatraceView(
        ALMETLAA_SCREEN.PROFILE,
        ALMETLAA_ACTION.PROFILE.ID_PROOF_VIEW,
        isAdhaJourney,
    );

    const emiratesId = user?.emairates_id || '';
    const passportNumber = user?.passport_number || '';

    const handleViewPassport = () => {
        // Navigate to document viewer for passport
        router.push({
            pathname: '/document-viewer',
            params: {
                documentType: 'passport',
            },
        } as any);
    };

    const handleViewEmiratesId = () => {
        // Navigate to document viewer for Emirates ID
        router.push({
            pathname: '/document-viewer',
            params: {
                documentType: 'emiratesIdFront',
            },
        } as any);
    };

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            <LogoHeader hideLogo={true}  showLanguageSwitch={false}/>

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={[
                    styles.content,
                    { paddingBottom: insets.bottom + 24 },
                ]}
                showsVerticalScrollIndicator={false}
            >
                {/* Title */}
                <Typography variant="title1" color="primary" style={styles.title}>
                    {t('profile.idProofs')}
                </Typography>

                {/* Passport card */}
                {Boolean(passportNumber) && (
                    <IdProofCard
                        title={t('profile.passport')}
                        value={passportNumber}
                        onView={handleViewPassport}
                    />
                )}

                {/* Emirates ID card */}
                {Boolean(emiratesId) && (
                    <IdProofCard
                        title={t('profile.emiratesId')}
                        value={emiratesId}
                        onView={handleViewEmiratesId}
                    />
                )}

                {/* Empty state */}
                {!emiratesId && !passportNumber && (
                    <View
                        style={{
                            borderRadius: 12,
                            paddingHorizontal: 16,
                            paddingTop: 16,
                            paddingBottom: 8,
                            gap: 8,
                            backgroundColor: colors.backgroundLevel2,
                        }}
                    >
                        <Typography variant="body" color="tertiary" style={styles.emptyStateText}>
                            {t('profile.noIdProofsAvailable')}
                        </Typography>
                    </View>
                )}
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
    },
    content: {
        paddingTop: 32,
        paddingHorizontal: 16,
        gap: 32,
    },
    title: {
        marginBottom: 0,
    },
    emptyStateText: {
        textAlign: 'center',
        paddingVertical: 16,
    },
});
